import React, { FC, useCallback } from "react";
import { useI18nMessage } from "@lookiero/i18n-react";
import { CommandStatus } from "@lookiero/messaging-react";
import { useLogger } from "@lookiero/sty-psp-logging";
import {
  isChildQuestion,
  ScreenProjection,
  useAnswersForQuestion,
  useAnswersValidation,
} from "@lookiero/sty-psp-quiz-style-profile-common-ui";
import { Button } from "@lookiero/sty-psp-ui";
import { Answers } from "../../../../../../../domain/screen/model/screen";
import { useFillScreen } from "../../../../../../domain/screen/react/useFillScreen";
import { useTrackFillScreen } from "../../../../../../tracking/useTrackFillScreen";
import { useTrackQuestionError } from "../../../../../../tracking/useTrackQuestionError";
import { useStaticInfo } from "../../../../../hooks/useStaticInfo";
import { DOMAIN } from "../../../../../i18n/i18n";

interface HostScreenOnSuccessFunctionArgs {
  readonly answers: Answers;
}

interface HostScreenOnSuccessFunction {
  (args: HostScreenOnSuccessFunctionArgs): void;
}

interface HostScreenCtaProps {
  readonly question: ScreenProjection;
  readonly onSuccess: HostScreenOnSuccessFunction;
}

const HostScreenCta: FC<HostScreenCtaProps> = ({ question, onSuccess }) => {
  const {
    customer: { customerId, country, segment },
    tradename,
  } = useStaticInfo();
  const logger = useLogger();

  const { validateHostScreen, validation } = useAnswersValidation();
  const text = useI18nMessage({
    domain: DOMAIN,
    id: question.metadata.ctaTranslationKey,
    values: { segment: segment.toLowerCase() },
  });

  const trackQuestionError = useTrackQuestionError({
    group: question.metadata.groupTranslationKey,
    country,
    segment,
    slug: question.metadata.slug,
    customerId,
    tradename,
  });

  const [fillScreen, fillScreenStatus] = useFillScreen({
    customerId: customerId,
    slug: question.metadata.slug,
    logger,
  });
  const trackFillQuiz = useTrackFillScreen({
    country,
    segment,
    slug: question.metadata.slug,
    customerId,
    tradename,
  });

  const { answers: questionAnswers } = useAnswersForQuestion({ question });

  const handleOnPress = useCallback(async () => {
    validateHostScreen({ questionId: question.id });

    trackQuestionError({ validation });

    const hasValidationError = validation?.reduce(
      (acc, v) => acc || isChildQuestion({ questionId: v.questionId, question }),
      false,
    );

    if (hasValidationError) {
      return;
    }

    const filled = await fillScreen({ answers: questionAnswers });

    if (!filled) {
      return;
    }

    trackFillQuiz({
      group: question.metadata.groupTranslationKey,
      question: question.metadata.slug,
      answers: questionAnswers,
    });

    onSuccess({ answers: questionAnswers });
  }, [
    fillScreen,
    onSuccess,
    question,
    questionAnswers,
    trackFillQuiz,
    trackQuestionError,
    validateHostScreen,
    validation,
  ]);

  return (
    <Button busy={fillScreenStatus === CommandStatus.LOADING} testID="host-screen-cta" onPress={handleOnPress}>
      {text}
    </Button>
  );
};

export type { HostScreenOnSuccessFunction };
export { HostScreenCta };
